import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Home, ChevronRight, AlertCircle, FileEdit, FileText, Loader2, User, Search, X, Scissors, GitBranch, Package, Plus, Trash2, Printer, ArrowDown } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import ProcessFlowDiagram from '@/components/ProcessFlowDiagram';
import PullFromStockModal from '@/components/PullFromStockModal';
import { formatDate } from '@/utils/formatters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUSES = ['Not Ready', 'Ready', 'In Progress'];

const STATUS_COLORS = {
  'Not Ready': 'bg-muted/50 border-muted',
  'Ready': 'bg-blue-950/50 border-blue-800',
  'In Progress': 'bg-orange-950/50 border-orange-800',
  'Complete': 'bg-green-950/50 border-green-800'
};

const KanbanBoard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [processes, setProcesses] = useState([]);
  const [worksOrders, setWorksOrders] = useState([]);
  const [selectedProcess, setSelectedProcess] = useState('');
  const [loading, setLoading] = useState(true);
  const [draggedOrder, setDraggedOrder] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [processData, setProcessData] = useState({});
  const [processOptions, setProcessOptions] = useState({});
  const [selectedProcessForEdit, setSelectedProcessForEdit] = useState(null);
  const [selectedProcessIndex, setSelectedProcessIndex] = useState(null);
  const [partDrawings, setPartDrawings] = useState([]);
  const [partDetails, setPartDetails] = useState(null);
  const [shortfallDialogOpen, setShortfallDialogOpen] = useState(false);
  const [shortfallQuantity, setShortfallQuantity] = useState(0);
  const [pendingShortfallOrder, setPendingShortfallOrder] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [splitDialogOpen, setSplitDialogOpen] = useState(false);
  const [splitData, setSplitData] = useState({ split_quantity: '', continue_from_step_index: 0, reason: '' });
  const [splitTreeData, setSplitTreeData] = useState(null);
  const [splitTreeOpen, setSplitTreeOpen] = useState(false);
  const [packages, setPackages] = useState([]);
  const [packageSummary, setPackageSummary] = useState(null);
  const [packageDialogOpen, setPackageDialogOpen] = useState(false);
  const [newPackage, setNewPackage] = useState({ package_type: '', package_type_other: '', quantity: '', notes: '' });
  const [editingPackage, setEditingPackage] = useState(null);
  const [pullFromStockOpen, setPullFromStockOpen] = useState(false);

  // Helper to get status - supports both index-based (new) and name-based (legacy) formats
  const getProcessStatus = (order, index, processName) => {
    if (order.process_statuses[String(index)] !== undefined) {
      return order.process_statuses[String(index)];
    }
    return order.process_statuses[processName] || 'Not Ready';
  };

  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const [processRes, ordersRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/processes`),
        axios.get(`${BACKEND_URL}/api/works-orders`)
      ]);
      
      setProcesses(processRes.data.processes);
      setProcessOptions(processRes.data.process_options || {});
      // Filter out completed orders (all processes including Invoice are Complete)
      const activeOrders = ordersRes.data.filter(o => {
        if (o.current_status === 'Closed') return false;
        const seq = o.process_sequence;
        if (seq.length === 0) return true;
        const allDone = seq.every((p, idx) => {
          const status = o.process_statuses[String(idx)] || o.process_statuses[p];
          return status === 'Complete';
        });
        return !allDone;
      });
      setWorksOrders(activeOrders);
      
      if (!selectedProcess && processRes.data.processes.length > 0) {
        setSelectedProcess(processRes.data.processes[0]);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const getOrdersForProcessAndStatus = (processName, status) => {
    return worksOrders.filter(order => {
      // Find ALL occurrences of this process (handles repeated steps like multiple machining ops)
      let matchFound = false;
      for (let i = 0; i < order.process_sequence.length; i++) {
        if (order.process_sequence[i] === processName) {
          const processStatus = getProcessStatus(order, i, processName);
          if (processStatus === status) {
            matchFound = true;
            break;
          }
        }
      }
      if (!matchFound) return false;
      // Apply search filter
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        return (
          order.order_number.toLowerCase().includes(q) ||
          order.customer.toLowerCase().includes(q) ||
          order.part_number.toLowerCase().includes(q) ||
          (order.customer_order_number || '').toLowerCase().includes(q)
        );
      }
      return true;
    });
  };

  const handleDragStart = (e, order, currentStatus) => {
    setDraggedOrder({ order, currentStatus });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, newStatus) => {
    e.preventDefault();
    
    if (!draggedOrder) return;
    
    const { order, currentStatus } = draggedOrder;
    
    if (currentStatus === newStatus) {
      setDraggedOrder(null);
      return;
    }

    try {
      // Find the correct index of the current process (handles repeated steps)
      // We need the index whose status matches what we're dragging FROM
      let currentProcessIndex = -1;
      for (let i = 0; i < order.process_sequence.length; i++) {
        if (order.process_sequence[i] === selectedProcess) {
          const s = order.process_statuses[String(i)] || 'Not Ready';
          if (s === currentStatus) {
            currentProcessIndex = i;
            break;
          }
        }
      }
      // Fallback to first occurrence if no exact match
      if (currentProcessIndex === -1) {
        currentProcessIndex = order.process_sequence.indexOf(selectedProcess);
      }
      
      // Update the current process status using index
      const updatedStatuses = { ...order.process_statuses };
      updatedStatuses[String(currentProcessIndex)] = newStatus;
      
      // If marking as Complete, check if we need to advance the next process
      if (newStatus === 'Complete') {
        if (currentProcessIndex !== -1 && currentProcessIndex + 1 < order.process_sequence.length) {
          const nextIdx = String(currentProcessIndex + 1);
          // Set next process to Ready if it's currently Not Ready
          if (updatedStatuses[nextIdx] === 'Not Ready' || !updatedStatuses[nextIdx]) {
            updatedStatuses[nextIdx] = 'Ready';
            const nextProcess = order.process_sequence[currentProcessIndex + 1];
            toast.success(`${order.order_number} moved to ${newStatus}. ${nextProcess} is now Ready`);
          }
        }
      }
      
      const response = await axios.put(`${BACKEND_URL}/api/works-orders/${order.order_number}`, {
        process_statuses: updatedStatuses
      });
      
      // Check if the order was auto-closed
      if (response.data?.current_status === 'Closed') {
        toast.success(`${order.order_number} is now CLOSED — all processes complete`);
      } else if (newStatus !== 'Complete') {
        toast.success(`${order.order_number} moved to ${newStatus}`);
      }
      
      fetchData();
    } catch (error) {
      console.error('Error updating order:', error);
      toast.error('Failed to update order');
    }
    
    setDraggedOrder(null);
  };

  const handleOrderClick = (order, e) => {
    // Don't open dialog if dragging
    if (e?.type === 'click') {
      setSelectedOrder(order);
      setProcessData(order.process_data || {});
      setSelectedProcessForEdit(null);
      setSelectedProcessIndex(null);
      setDialogOpen(true);
      
      // Fetch part drawings
      fetchPartDrawings(order.part_number);
      // Fetch packages
      fetchPackages(order.order_number);
    }
  };

  const fetchPartDrawings = async (partNumber) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/parts/${partNumber}`);
      setPartDrawings(response.data.drawings || []);
      setPartDetails(response.data);
    } catch (error) {
      console.error('Error fetching part drawings:', error);
      setPartDrawings([]);
      setPartDetails(null);
    }
  };

  const handleProcessFlowClick = (processName, processIndex) => {
    setSelectedProcessForEdit(processName);
    setSelectedProcessIndex(processIndex);
  };

  const handleProcessDataChange = (processName, field, value) => {
    // Use index as key to support duplicate process steps
    const key = String(selectedProcessIndex);
    setProcessData(prev => ({
      ...prev,
      [key]: {
        ...(prev[key] || {}),
        [field]: value
      }
    }));
  };

  const handleCompleteCurrentStep = async () => {
    if (!selectedOrder) return;
    
    // Find the current "In Progress" step by index
    let inProgressIndex = -1;
    for (let i = 0; i < selectedOrder.process_sequence.length; i++) {
      const status = getProcessStatus(selectedOrder, i, selectedOrder.process_sequence[i]);
      if (status === 'In Progress') {
        inProgressIndex = i;
        break;
      }
    }

    if (inProgressIndex === -1) {
      toast.error('No step is currently in progress');
      return;
    }

    const processName = selectedOrder.process_sequence[inProgressIndex];
    
    try {
      // Save process data first (so any entered info is preserved)
      if (Object.keys(processData).length > 0) {
        await axios.put(`${BACKEND_URL}/api/works-orders/${selectedOrder.order_number}`, {
          process_data: processData
        });
      }

      const updatedStatuses = { ...selectedOrder.process_statuses };
      updatedStatuses[String(inProgressIndex)] = 'Complete';
      
      // Auto-advance next step to Ready
      if (inProgressIndex + 1 < selectedOrder.process_sequence.length) {
        const nextIdx = String(inProgressIndex + 1);
        if (updatedStatuses[nextIdx] === 'Not Ready' || !updatedStatuses[nextIdx]) {
          updatedStatuses[nextIdx] = 'Ready';
        }
      }
      
      const response = await axios.put(`${BACKEND_URL}/api/works-orders/${selectedOrder.order_number}`, {
        process_statuses: updatedStatuses
      });
      
      // Check if the order was auto-closed
      if (response.data?.current_status === 'Closed') {
        toast.success(`${selectedOrder.order_number} is now CLOSED — all processes complete`);
      } else {
        toast.success(`${processName} completed for ${selectedOrder.order_number}`);
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error completing step:', error);
      toast.error('Failed to complete step');
    }
  };

  const openSplitDialog = () => {
    if (!selectedOrder) return;
    // Default: split from the next incomplete step
    let defaultStep = 0;
    for (let i = 0; i < selectedOrder.process_sequence.length; i++) {
      const s = getProcessStatus(selectedOrder, i, selectedOrder.process_sequence[i]);
      if (s !== 'Complete') { defaultStep = i; break; }
    }
    setSplitData({ split_quantity: '', continue_from_step_index: defaultStep, reason: '' });
    setSplitDialogOpen(true);
  };

  const handleSplitOrder = async () => {
    if (!selectedOrder || !splitData.split_quantity) return;
    const qty = parseInt(splitData.split_quantity);
    if (isNaN(qty) || qty <= 0 || qty >= selectedOrder.quantity) {
      toast.error(`Split quantity must be between 1 and ${selectedOrder.quantity - 1}`);
      return;
    }
    try {
      const res = await axios.post(`${BACKEND_URL}/api/works-orders/${selectedOrder.order_number}/split`, {
        split_quantity: qty,
        continue_from_step_index: splitData.continue_from_step_index,
        reason: splitData.reason,
        split_by: user?.email || ''
      });
      toast.success(res.data.message);
      setSplitDialogOpen(false);
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to split order');
    }
  };

  const fetchSplitTree = async (orderNumber) => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/works-orders/${orderNumber}/split-tree`);
      setSplitTreeData(res.data);
      setSplitTreeOpen(true);
    } catch (error) {
      toast.error('Failed to load split tree');
    }
  };

  const fetchPackages = async (orderNumber) => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/works-orders/${orderNumber}/packages`);
      setPackages(res.data.packages);
      setPackageSummary(res.data.summary);
    } catch {
      setPackages([]);
      setPackageSummary(null);
    }
  };

  const handleCreatePackage = async () => {
    if (!selectedOrder || !newPackage.quantity || !newPackage.package_type) return;
    try {
      await axios.post(`${BACKEND_URL}/api/works-orders/${selectedOrder.order_number}/packages`, {
        package_type: newPackage.package_type,
        package_type_other: newPackage.package_type_other,
        quantity: parseInt(newPackage.quantity),
        created_by: user?.email || '',
        notes: newPackage.notes
      });
      toast.success('Package unit created');
      setPackageDialogOpen(false);
      setNewPackage({ package_type: '', package_type_other: '', quantity: '', notes: '' });
      fetchPackages(selectedOrder.order_number);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create package');
    }
  };

  const handleDeletePackage = async (pkgId) => {
    if (!selectedOrder) return;
    if (!window.confirm(`Delete package ${pkgId}?`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/works-orders/${selectedOrder.order_number}/packages/${pkgId}`);
      toast.success(`Package ${pkgId} deleted`);
      fetchPackages(selectedOrder.order_number);
    } catch (error) {
      toast.error('Failed to delete package');
    }
  };

  const handleUpdatePackage = async () => {
    if (!editingPackage) return;
    try {
      await axios.put(
        `${BACKEND_URL}/api/works-orders/${selectedOrder.order_number}/packages/${editingPackage.package_id}`,
        { quantity: parseInt(editingPackage.quantity), notes: editingPackage.notes }
      );
      toast.success('Package updated');
      setEditingPackage(null);
      fetchPackages(selectedOrder.order_number);
    } catch (error) {
      toast.error('Failed to update package');
    }
  };

  const handlePrintPackingSlip = async (pkg) => {
    if (!selectedOrder) return;
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/works-orders/${selectedOrder.order_number}/packages/${pkg.package_id}/packing-slip?generated_by=${encodeURIComponent(user?.email || 'User')}`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `PS-${selectedOrder.order_number}-${pkg.package_id.split('-').pop()}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Packing slip generated for ${pkg.package_id}`);
      // Refresh packages to update print count
      fetchPackages(selectedOrder.order_number);
    } catch {
      toast.error('Failed to generate packing slip');
    }
  };

  const handlePrintWoPackingSlip = async () => {
    if (!selectedOrder) return;
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/works-orders/${selectedOrder.order_number}/packing-slip?generated_by=${encodeURIComponent(user?.email || 'User')}`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `PS-${selectedOrder.order_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`WO Packing Slip generated for ${selectedOrder.order_number}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate packing slip');
    }
  };

  const handleSaveProcessData = async () => {
    try {
      await axios.put(`${BACKEND_URL}/api/works-orders/${selectedOrder.order_number}`, {
        process_data: processData
      });
      toast.success('Process data saved successfully');
      
      // Check for shortfall if saving Final Inspection data
      if (selectedProcessForEdit === 'Final Inspection') {
        const finalInspectionData = processData[String(selectedProcessIndex)] || {};
        const inspectedQty = parseInt(finalInspectionData.quantity) || 0;
        const orderQty = selectedOrder.quantity || 0;
        
        if (inspectedQty > 0 && inspectedQty < orderQty) {
          const shortfall = orderQty - inspectedQty;
          setShortfallQuantity(shortfall);
          setPendingShortfallOrder(selectedOrder);
          setShortfallDialogOpen(true);
          setDialogOpen(false);
          return;
        }
      }
      
      setDialogOpen(false);
      
      // Update data without changing selected process tab
      const [processRes, ordersRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/processes`),
        axios.get(`${BACKEND_URL}/api/works-orders`)
      ]);
      
      setProcesses(processRes.data.processes);
      setWorksOrders(ordersRes.data);
      // selectedProcess remains unchanged, keeping user on current tab
    } catch (error) {
      console.error('Error saving process data:', error);
      toast.error('Failed to save process data');
    }
  };

  const handleCreateShortfallOrder = async () => {
    if (!pendingShortfallOrder) return;
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/works-orders`, {
        customer: pendingShortfallOrder.customer,
        customer_order_number: pendingShortfallOrder.customer_order_number ? `${pendingShortfallOrder.customer_order_number}-SF` : '',
        part_number: pendingShortfallOrder.part_number,
        quantity: shortfallQuantity,
        unit_price: pendingShortfallOrder.unit_price || 0,
        due_date: pendingShortfallOrder.due_date,
        notes: `Shortfall order from ${pendingShortfallOrder.order_number}. Original qty: ${pendingShortfallOrder.quantity}, Delivered: ${pendingShortfallOrder.quantity - shortfallQuantity}`,
        process_sequence: pendingShortfallOrder.process_sequence
      });
      
      toast.success(`Shortfall order ${response.data.order_number} created for ${shortfallQuantity} units`);
      setShortfallDialogOpen(false);
      setPendingShortfallOrder(null);
      setShortfallQuantity(0);
      fetchData();
    } catch (error) {
      console.error('Error creating shortfall order:', error);
      toast.error('Failed to create shortfall order');
    }
  };

  const handleDeclineShortfallOrder = () => {
    setShortfallDialogOpen(false);
    setPendingShortfallOrder(null);
    setShortfallQuantity(0);
    fetchData();
  };

  const calculateBarsRequired = () => {
    if (!partDetails || !selectedOrder) return null;
    
    // Standard bar length is 5800mm
    const standardBarLength = 5800;
    const loc = partDetails.loc || 0;
    const quantity = selectedOrder.quantity || 0;
    
    if (loc <= 0) return quantity; // Fallback to quantity if no LOC
    
    // Pieces per bar = floor(5800 / (LOC + 2mm kerf allowance))
    const piecesPerBar = Math.floor(standardBarLength / (loc + 2));
    if (piecesPerBar <= 0) return quantity;
    
    const barsRequired = Math.ceil(quantity / piecesPerBar);
    
    return barsRequired;
  };

  const getProcessFields = (processName) => {
    const options = processOptions[processName];
    
    // Define fields for each process based on requirements
    switch (processName) {
      case 'Material Sourcing':
        return [
          { name: 'supplier', label: 'Supplier', type: 'text' },
          { name: 'po_number', label: 'Purchase Order Number', type: 'text' },
          { name: 'bars_ordered', label: 'Bars Ordered', type: 'number' }
        ];
      
      case 'Cutting':
        return [
          { name: 'cast_number', label: 'Material Cast Number', type: 'text' },
          { name: 'bundle_number', label: 'Bundle Number', type: 'text' },
          { name: 'notes', label: 'Notes', type: 'text' }
        ];
      
      case 'Face and Center':
        const fcFields = [];
        if (options?.options) {
          fcFields.push({
            name: 'equipment',
            label: 'Equipment',
            type: 'select',
            options: options.options
          });
        }
        fcFields.push({ name: 'operator', label: 'Operator Name', type: 'text' });
        return fcFields;
      
      case 'Forging':
        const forgingFields = [];
        if (options?.options) {
          forgingFields.push({
            name: 'equipment',
            label: 'Equipment',
            type: 'select',
            options: options.options
          });
        }
        forgingFields.push(
          { name: 'operator', label: 'Operator Name', type: 'text' },
          { name: 'quantity_scrap', label: 'Quantity Scrap', type: 'number' }
        );
        return forgingFields;
      
      case 'Machining':
        return [
          { name: 'machine_id', label: 'Machine ID', type: 'text' },
          { name: 'operator', label: 'Operator Name', type: 'text' },
          { name: 'cycle_time', label: 'Cycle Time', type: 'text' },
          { name: 'quantity_scrap', label: 'Quantity Scrap', type: 'number' },
          { name: 'notes', label: 'Notes', type: 'text' }
        ];
      
      case 'Milling':
        return [
          { name: 'machine_id', label: 'Machine ID', type: 'text' },
          { name: 'operator', label: 'Operator Name', type: 'text' },
          { name: 'cycle_time', label: 'Cycle Time', type: 'text' },
          { name: 'quantity_scrap', label: 'Quantity Scrap', type: 'number' },
          { name: 'notes', label: 'Notes', type: 'text' }
        ];
      
      case 'Heat Treatment':
        const htFields = [];
        if (options?.suppliers) {
          htFields.push({
            name: 'supplier',
            label: 'Supplier',
            type: 'select',
            options: options.suppliers
          });
        }
        if (options?.specifications) {
          htFields.push({
            name: 'specification',
            label: 'Specification',
            type: 'select',
            options: options.specifications
          });
        }
        htFields.push(
          { name: 'delivery_note_number', label: 'Delivery Note Number', type: 'text' },
          { name: 'ht_certificate_number', label: 'Heat Treatment Certificate Number', type: 'text' },
          { name: 'notes', label: 'Notes', type: 'text' }
        );
        return htFields;
      
      case 'Surface Coating':
        const scFields = [];
        if (options?.suppliers) {
          scFields.push({
            name: 'supplier',
            label: 'Supplier',
            type: 'select',
            options: options.suppliers
          });
        }
        if (options?.specifications) {
          scFields.push({
            name: 'specification',
            label: 'Specification',
            type: 'select',
            options: options.specifications
          });
        }
        scFields.push(
          { name: 'delivery_note_number', label: 'Delivery Note Number', type: 'text' },
          { name: 'sc_certificate_number', label: 'Surface Coating Certificate Number', type: 'text' },
          { name: 'notes', label: 'Notes', type: 'text' }
        );
        return scFields;
      
      case 'Other Subcontracted Services':
        return [
          { name: 'supplier', label: 'Supplier', type: 'text' },
          { name: 'delivery_note_number', label: 'Delivery Note Number', type: 'text' },
          { name: 'notes', label: 'Notes', type: 'text' }
        ];
      
      case 'Final Inspection':
        return [
          { name: 'quantity', label: 'Quantity', type: 'number' },
          { name: 'inspector_id', label: 'Inspector ID', type: 'text' },
          { name: 'other_scrap', label: 'Other Scrap', type: 'number' },
          { name: 'delivery_method', label: 'Delivery Method', type: 'text' },
          { name: 'notes', label: 'Notes', type: 'text' }
        ];
      
      case 'Invoice':
        return [
          { name: 'invoice_number', label: 'Invoice Number', type: 'text' },
          { name: 'delivery_note_number', label: 'Delivery Note Number', type: 'text' },
          { name: 'delivery_method', label: 'Delivery Method', type: 'text' },
          { name: 'invoice_date', label: 'Invoice Date', type: 'date' }
        ];
      
      default:
        return [{ name: 'notes', label: 'Notes', type: 'text' }];
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] relative overflow-hidden text-white">
      {/* Honeycomb Background */}
      <div className="fixed inset-0 opacity-30 pointer-events-none">
        <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="honeycomb-kanban" x="0" y="0" width="56" height="100" patternUnits="userSpaceOnUse">
              <path d="M28 0L56 16.5V49.5L28 66L0 49.5V16.5L28 0Z" fill="none" stroke="rgba(6, 182, 212, 0.12)" strokeWidth="1"/>
              <path d="M28 66L56 82.5V115.5L28 132L0 115.5V82.5L28 66Z" fill="none" stroke="rgba(6, 182, 212, 0.12)" strokeWidth="1"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#honeycomb-kanban)" />
        </svg>
      </div>
      <div className="fixed inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at center, rgba(6, 182, 212, 0.06) 0%, transparent 70%)' }} />

      {/* Fixed Header */}
      <header className="fixed top-0 left-0 right-0 z-50 h-16 bg-slate-950/90 backdrop-blur-xl border-b border-cyan-500/20">
        <div className="h-full max-w-[1920px] mx-auto px-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/')} className="text-slate-400 hover:text-cyan-400 hover:bg-cyan-500/10" data-testid="back-btn">
              <Home className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center">
                <FileEdit className="w-5 h-5 text-cyan-400" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-white uppercase tracking-wide" data-testid="page-title">Workflow</h1>
                <p className="text-xs text-slate-500">Kanban Process Queues</p>
              </div>
            </div>
          </div>
          <nav className="hidden md:flex items-center gap-2 text-sm">
            <span className="text-slate-500">Home</span>
            <ChevronRight className="w-4 h-4 text-slate-600" />
            <span className="text-cyan-400 font-medium">Production</span>
          </nav>
          <div className="hidden sm:flex items-center gap-2 text-sm">
            <div className="w-8 h-8 rounded-full bg-cyan-500/20 border border-cyan-500/30 flex items-center justify-center">
              <User className="w-4 h-4 text-cyan-400" />
            </div>
            <span className="text-slate-400">{user?.name || user?.email?.split('@')[0]}</span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 pt-20 px-4 sm:px-6 pb-8">
        <div className="max-w-[1800px] mx-auto">

        <Tabs value={selectedProcess} onValueChange={setSelectedProcess} className="w-full">
          <TabsList className="bg-slate-900/40 border border-cyan-500/10 mb-6 flex-wrap h-auto backdrop-blur-sm" data-testid="process-tabs">
            {processes.map((proc) => (
              <TabsTrigger
                key={proc}
                value={proc}
                data-testid={`process-tab-${proc}`}
                className="data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400 data-[state=active]:border-cyan-500/50 font-mono text-xs uppercase tracking-wider border border-transparent"
              >
                {proc}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* Search Bar */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by order, customer, part, or PO number..."
              className="w-full pl-10 pr-10 py-2.5 bg-slate-900/60 border border-slate-800 rounded-lg text-sm text-white placeholder-slate-600 focus:outline-none focus:border-cyan-500/50 font-mono"
              data-testid="kanban-search"
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {processes.map((proc) => (
            <TabsContent key={proc} value={proc} className="mt-0" data-testid={`process-content-${proc}`}>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {STATUSES.map((status) => {
                  const orders = getOrdersForProcessAndStatus(proc, status);
                  
                  return (
                    <div
                      key={status}
                      className="flex flex-col"
                      data-testid={`kanban-column-${status}`}
                    >
                      <div className={`backdrop-blur-sm border rounded-lg p-4 mb-3 ${
                        status === 'Not Ready' ? 'bg-slate-900/40 border-slate-700' :
                        status === 'Ready' ? 'bg-blue-500/10 border-blue-500/30' :
                        status === 'In Progress' ? 'bg-orange-500/10 border-orange-500/30' :
                        'bg-green-500/10 border-green-500/30'
                      }`}>
                        <h3 className={`text-sm font-bold uppercase tracking-wider ${
                          status === 'Not Ready' ? 'text-slate-400' :
                          status === 'Ready' ? 'text-blue-400' :
                          status === 'In Progress' ? 'text-orange-400' :
                          'text-green-400'
                        }`}>
                          {status}
                        </h3>
                        <span className="text-xs text-slate-500 font-mono">
                          {orders.length} {orders.length === 1 ? 'order' : 'orders'}
                        </span>
                      </div>

                      <div
                        className="flex-1 bg-slate-900/20 border border-slate-800/50 rounded-lg p-2 min-h-[400px] kanban-column-scroll"
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDrop(e, status)}
                        data-testid={`kanban-drop-zone-${status}`}
                      >
                        <div className="space-y-2">
                          {orders.map((order) => {
                            const processIndex = order.process_sequence.indexOf(proc);
                            const isCurrentStep = order.current_step_index === processIndex;
                            
                            return (
                              <div
                                key={order.order_number}
                                draggable
                                onDragStart={(e) => handleDragStart(e, order, status)}
                                onClick={(e) => handleOrderClick(order, e)}
                                className={`bg-slate-900/60 backdrop-blur-sm border rounded-lg p-4 cursor-move hover:border-cyan-500/50 transition-all duration-300 hover:shadow-[0_0_20px_rgba(6,182,212,0.1)] relative group ${
                                  order.is_trial ? 'border-red-500/60 shadow-[0_0_12px_rgba(239,68,68,0.15)]' : 'border-slate-800'
                                }`}
                                data-testid={`order-card-${order.order_number}`}
                              >
                                {order.is_trial && (
                                  <div className="absolute -top-2 left-3 px-2 py-0.5 bg-red-500 text-white text-[10px] font-bold uppercase tracking-widest rounded" data-testid={`trial-badge-${order.order_number}`}>
                                    Trial
                                  </div>
                                )}
                                {order.parent_order_number && (
                                  <div className="absolute -top-2 right-3 px-2 py-0.5 bg-purple-600 text-white text-[10px] font-bold uppercase tracking-widest rounded flex items-center gap-1" data-testid={`split-badge-${order.order_number}`}>
                                    <GitBranch className="w-2.5 h-2.5" /> Split
                                  </div>
                                )}
                                {order.split_children && order.split_children.length > 0 && !order.parent_order_number && (
                                  <div className="absolute -top-2 right-3 px-2 py-0.5 bg-indigo-600 text-white text-[10px] font-bold uppercase tracking-widest rounded">
                                    Parent ({order.split_children.length})
                                  </div>
                                )}
                                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <FileEdit className="w-3 h-3 text-slate-500" />
                                </div>
                                
                                <div className="flex items-start justify-between mb-2">
                                  <span className="font-mono text-white font-bold text-sm" data-testid="order-number">
                                    {order.order_number}
                                  </span>
                                  {order.shortage_flagged && (
                                    <AlertCircle className="w-4 h-4 text-red-400" data-testid="shortage-flag" />
                                  )}
                                </div>
                                
                                <div className="space-y-1 text-xs">
                                  <div>
                                    <span className="text-slate-500">Part:</span>
                                    <span className="text-white ml-1 font-mono" data-testid="order-part">{order.part_number}</span>
                                  </div>
                                  <div>
                                    <span className="text-slate-500">Customer:</span>
                                    <span className="text-white ml-1" data-testid="order-customer">{order.customer}</span>
                                  </div>
                                  {order.customer_order_number && (
                                    <div>
                                      <span className="text-slate-500">PO:</span>
                                      <span className="text-amber-400 ml-1 font-mono" data-testid="order-po">{order.customer_order_number}</span>
                                    </div>
                                  )}
                                  <div>
                                    <span className="text-slate-500">Qty:</span>
                                    <span className="text-white ml-1" data-testid="order-quantity">{order.quantity}</span>
                                    {order.original_quantity && order.original_quantity !== order.quantity && (
                                      <span className="text-slate-600 ml-1 text-[10px]">(of {order.original_quantity})</span>
                                    )}
                                  </div>
                                  <div>
                                    <span className="text-slate-500">Due:</span>
                                    <span className="text-white ml-1">{formatDate(order.due_date)}</span>
                                  </div>
                                  {order.parent_order_number && (
                                    <div>
                                      <span className="text-slate-500">From:</span>
                                      <span className="text-purple-400 ml-1 font-mono">{order.parent_order_number}</span>
                                    </div>
                                  )}
                                </div>
                                
                                {isCurrentStep && (
                                  <div className="mt-2 pt-2 border-t border-cyan-500/30">
                                    <span className="text-xs text-cyan-400 font-bold uppercase">Active Step</span>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </TabsContent>
          ))}
        </Tabs>

        {/* Data Input Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-slate-950 border-cyan-500/30 max-w-5xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="process-data-dialog">
            <DialogHeader className="flex-shrink-0">
              <DialogTitle className="text-white uppercase tracking-tight text-xl">
                {selectedOrder?.order_number} - Process Data
              </DialogTitle>
            </DialogHeader>

            {selectedOrder && (
              <>
                <div className="space-y-6 overflow-y-auto flex-1 pr-2">
                {/* Order Summary Section */}
                <div className="bg-slate-900/60 border border-slate-800 rounded-lg p-4">
                  <h4 className="text-white font-bold uppercase tracking-wide mb-3 text-sm border-b border-slate-800 pb-2">
                    Order Summary
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-slate-500 text-xs uppercase tracking-wider">Part Number</span>
                      <p className="text-white font-mono font-bold">{selectedOrder.part_number}</p>
                    </div>
                    <div>
                      <span className="text-slate-500 text-xs uppercase tracking-wider">Customer</span>
                      <p className="text-white font-bold">{selectedOrder.customer}</p>
                    </div>
                    <div>
                      <span className="text-slate-500 text-xs uppercase tracking-wider">Order Quantity</span>
                      <p className="text-cyan-400 font-mono font-bold text-lg">{selectedOrder.quantity}</p>
                    </div>
                    <div>
                      <span className="text-slate-500 text-xs uppercase tracking-wider">Due Date</span>
                      <p className="text-white font-bold">{formatDate(selectedOrder.due_date)}</p>
                    </div>
                  </div>
                </div>

                {/* Part Details Section */}
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4" data-testid="part-details-section">
                  <h4 className="text-blue-400 font-bold uppercase tracking-wide mb-3 text-sm border-b border-blue-500/20 pb-2 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Part Details (Engineering Specification)
                  </h4>
                  {partDetails ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                      <div className="col-span-2 md:col-span-3">
                        <span className="text-slate-500 text-xs uppercase tracking-wider">Part Description</span>
                        <p className="text-white font-bold" data-testid="part-description">{partDetails.description || 'N/A'}</p>
                      </div>
                      <div>
                        <span className="text-slate-500 text-xs uppercase tracking-wider">Material Grade</span>
                        <p className="text-white font-mono font-bold text-lg" data-testid="part-material-grade">{partDetails.material_grade || 'N/A'}</p>
                      </div>
                      <div>
                        <span className="text-slate-500 text-xs uppercase tracking-wider">Diameter</span>
                        <p className="text-white font-mono font-bold text-lg" data-testid="part-diameter">
                          {partDetails.diameter ? `${partDetails.diameter} mm` : 'N/A'}
                        </p>
                      </div>
                      <div>
                        <span className="text-slate-500 text-xs uppercase tracking-wider">Length of Cut (LOC)</span>
                        <p className="text-white font-mono font-bold text-lg" data-testid="part-loc">
                          {partDetails.loc ? `${partDetails.loc} mm` : 'N/A'}
                        </p>
                      </div>
                      <div className="col-span-2 md:col-span-3 bg-slate-900/60 p-3 rounded-lg border border-slate-800">
                        <span className="text-slate-500 text-xs uppercase tracking-wider">Heat Treatment Requirement</span>
                        <p className="text-white font-bold mt-1" data-testid="part-heat-treatment">
                          {partDetails.heat_treatment || 'Not specified'}
                        </p>
                      </div>
                      {/* Material Sourcing PO Number */}
                      {(() => {
                        const msIndex = selectedOrder.process_sequence?.indexOf('Material Sourcing');
                        const msData = msIndex !== -1 ? (selectedOrder.process_data?.[String(msIndex)] || selectedOrder.process_data?.['Material Sourcing'] || {}) : {};
                        return msData.po_number ? (
                          <div className="col-span-2 md:col-span-3 bg-green-500/10 p-3 rounded-lg border border-green-500/30">
                            <span className="text-green-400 text-xs uppercase tracking-wider">Material Sourcing Purchase Order</span>
                            <p className="text-white font-mono font-bold mt-1" data-testid="material-sourcing-po">
                              {msData.po_number}
                            </p>
                          </div>
                        ) : null;
                      })()}
                    </div>
                  ) : (
                    <div className="text-center py-4 text-slate-500">
                      <p>Loading part details...</p>
                    </div>
                  )}
                  {partDrawings.length > 0 && (
                    <div className="mt-4 pt-3 border-t border-blue-500/20">
                      <Button
                        onClick={() => {
                          partDrawings.forEach(drawing => {
                            window.open(`${BACKEND_URL}/api/parts/${selectedOrder.part_number}/drawings/${drawing.filepath}`, '_blank');
                          });
                        }}
                        className="bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 border border-blue-500/50 font-bold uppercase tracking-wide w-full"
                        data-testid="view-drawings-btn"
                      >
                        <FileText className="w-4 h-4 mr-2" />
                        View Engineering Drawings ({partDrawings.length})
                      </Button>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Process Flow Diagram */}
                  <div>
                    <h4 className="text-white font-bold uppercase tracking-wide mb-4 text-sm">
                      Process Flow
                    </h4>
                    <div className="bg-slate-900/40 border border-slate-800 rounded-lg p-4 max-h-[400px] overflow-y-auto">
                      <ProcessFlowDiagram
                        processSequence={selectedOrder.process_sequence}
                        processStatuses={selectedOrder.process_statuses}
                        onProcessClick={handleProcessFlowClick}
                      />
                    </div>
                  </div>

                  {/* Process Details Form */}
                  <div>
                    <h4 className="text-white font-bold uppercase tracking-wide mb-4 text-sm">
                      {selectedProcessForEdit ? `${selectedProcessForEdit} Details` : 'Select a process step'}
                    </h4>
                    <div className="bg-slate-900/40 border border-slate-800 rounded-lg p-4 max-h-[400px] overflow-y-auto">
                      {selectedProcessForEdit ? (
                        <div className="space-y-4">
                          <div className="flex items-center justify-between mb-4 pb-4 border-b border-slate-800">
                            <span className={`text-xs px-3 py-1 rounded-lg font-bold ${
                              getProcessStatus(selectedOrder, selectedProcessIndex, selectedProcessForEdit) === 'Complete' ? 'bg-green-500/20 text-green-400 border border-green-500/30' :
                              getProcessStatus(selectedOrder, selectedProcessIndex, selectedProcessForEdit) === 'In Progress' ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30' :
                              getProcessStatus(selectedOrder, selectedProcessIndex, selectedProcessForEdit) === 'Ready' ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' :
                              'bg-slate-800 text-slate-400 border border-slate-700'
                            }`}>
                              {getProcessStatus(selectedOrder, selectedProcessIndex, selectedProcessForEdit)}
                            </span>
                          </div>

                          {/* Material Sourcing Info Display */}
                          {selectedProcessForEdit === 'Material Sourcing' && partDetails && (
                            <div className="bg-slate-900/60 border border-slate-800 rounded-lg p-4 mb-4" data-testid="material-sourcing-info">
                              <h5 className="text-white font-bold uppercase tracking-wide text-xs mb-3 border-b border-slate-800 pb-2">
                                Material Requirements
                              </h5>
                              <div className="space-y-3">
                                <div className="flex justify-between items-center">
                                  <span className="text-slate-500 text-sm">Material Grade:</span>
                                  <span className="text-white font-mono font-bold">
                                    {partDetails.material_grade || 'N/A'}
                                  </span>
                                </div>
                                <div className="flex justify-between items-center">
                                  <span className="text-slate-500 text-sm">Diameter:</span>
                                  <span className="text-white font-mono font-bold">
                                    {partDetails.diameter ? `${partDetails.diameter} mm` : 'N/A'}
                                  </span>
                                </div>
                                <div className="flex justify-between items-center">
                                  <span className="text-slate-500 text-sm">Length of Cut (LOC):</span>
                                  <span className="text-white font-mono font-bold">
                                    {partDetails.loc ? `${partDetails.loc} mm` : 'N/A'}
                                  </span>
                                </div>
                                <div className="flex justify-between items-center pt-2 border-t border-slate-800">
                                  <span className="text-slate-500 text-sm">Order Quantity:</span>
                                  <span className="text-white font-mono font-bold">
                                    {selectedOrder.quantity} pcs
                                  </span>
                                </div>
                                <div className="flex justify-between items-center bg-cyan-500/10 p-2 rounded-lg border border-cyan-500/30">
                                  <span className="text-cyan-400 text-sm font-bold">Bars Required:</span>
                                  <span className="text-cyan-400 font-mono font-bold text-lg" data-testid="bars-required-value">
                                    {calculateBarsRequired() || 'N/A'}
                                  </span>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Pull from Stock button for Cutting */}
                          {selectedProcessForEdit === 'Cutting' && (
                            <div className="mb-4">
                              <Button
                                onClick={() => setPullFromStockOpen(true)}
                                className="w-full bg-green-500/20 hover:bg-green-500/30 text-green-400 border border-green-500/50 font-bold uppercase tracking-wide"
                                data-testid="pull-from-stock-btn"
                              >
                                <ArrowDown className="w-4 h-4 mr-2" />
                                Pull from Stock
                              </Button>
                              {partDetails && (
                                <p className="text-xs text-slate-500 mt-1 text-center">
                                  Requires: {partDetails.material_grade || '?'} — {partDetails.diameter || '?'} mm
                                </p>
                              )}
                            </div>
                          )}

                          {/* Show Heat Treatment Specification from Engineering */}
                          {selectedProcessForEdit === 'Heat Treatment' && partDetails && (
                            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 mb-4">
                              <Label className="text-blue-400 uppercase tracking-wider text-xs mb-2 block">
                                Heat Treatment Specification (from Engineering)
                              </Label>
                              <p className="text-white font-bold text-lg" data-testid="heat-treatment-spec">
                                {partDetails.heat_treatment || 'None specified'}
                              </p>
                            </div>
                          )}

                          {getProcessFields(selectedProcessForEdit).map((field) => (
                            <div key={field.name}>
                              <Label className="text-slate-400 uppercase tracking-wider text-xs mb-2">
                                {field.label}
                              </Label>
                              {field.type === 'select' ? (
                                <Select
                                  value={processData[String(selectedProcessIndex)]?.[field.name] || ''}
                                  onValueChange={(value) => handleProcessDataChange(selectedProcessForEdit, field.name, value)}
                                >
                                  <SelectTrigger className="bg-slate-950/50 border-slate-800">
                                    <SelectValue placeholder={`Select ${field.label.toLowerCase()}`} />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {field.options.map((option) => (
                                      <SelectItem key={option} value={option}>
                                        {option}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              ) : (
                                <Input
                                  type={field.type}
                                  value={processData[String(selectedProcessIndex)]?.[field.name] || ''}
                                  onChange={(e) => handleProcessDataChange(selectedProcessForEdit, field.name, e.target.value)}
                                  className="bg-slate-950/50 border-slate-800"
                                  placeholder={`Enter ${field.label.toLowerCase()}...`}
                                  data-testid={`${selectedProcessForEdit}-${field.name}-input`}
                                />
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-12 text-slate-500">
                          <p className="text-sm">Click on a process step in the flow diagram to edit its details</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Packaging Section - visible for all orders */}
              <div className="border border-purple-500/20 rounded-lg p-4 bg-purple-950/10">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-purple-400 font-bold uppercase tracking-wide text-sm flex items-center gap-2">
                    <Package className="w-4 h-4" />
                    Package Units / Batch Identification
                  </h4>
                  <div className="flex items-center gap-3">
                    {packageSummary && (
                      <div className="flex items-center gap-2 text-xs">
                        <span className={`px-2 py-0.5 rounded font-bold ${
                          packageSummary.status === 'fully_packed' ? 'bg-green-500/20 text-green-400 border border-green-500/30' :
                          packageSummary.status === 'partially_packed' ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30' :
                          'bg-red-500/20 text-red-400 border border-red-500/30'
                        }`} data-testid="packaging-status">
                          {packageSummary.status === 'fully_packed' ? 'FULLY PACKED' :
                           packageSummary.status === 'partially_packed' ? 'PARTIALLY PACKED' : 'NOT PACKED'}
                        </span>
                        <span className="text-slate-500">
                          {packageSummary.total_packed}/{packageSummary.wo_quantity}
                          {packageSummary.balance_remaining > 0 && <span className="text-orange-400 ml-1">(bal: {packageSummary.balance_remaining})</span>}
                        </span>
                        {packageSummary.total_packed > 0 && (
                          <Button size="sm" onClick={handlePrintWoPackingSlip} className="bg-green-600/20 hover:bg-green-600/30 text-green-400 border border-green-500/40 text-[10px] h-6 px-2" data-testid="print-wo-packing-slip-btn">
                            <Printer className="w-3 h-3 mr-1" /> Print Packing Slip
                          </Button>
                        )}
                      </div>
                    )}
                    <Button
                      size="sm"
                      onClick={() => { setNewPackage({ package_type: '', package_type_other: '', quantity: '', notes: '' }); setPackageDialogOpen(true); }}
                      className="bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 border border-purple-500/50 text-xs"
                      data-testid="create-package-btn"
                    >
                      <Plus className="w-3.5 h-3.5 mr-1" /> Create Package Unit
                    </Button>
                  </div>
                </div>

                {/* Traceability Info: Cast Number, HT Certificate & Customer PO */}
                {(() => {
                  const seq = selectedOrder.process_sequence || [];
                  const pd = selectedOrder.process_data || {};
                  const cuttingIdx = seq.indexOf('Cutting');
                  const htIdx = seq.indexOf('Heat Treatment');
                  const castNumber = cuttingIdx >= 0 ? (pd[String(cuttingIdx)]?.cast_number || pd['Cutting']?.cast_number) : null;
                  const htCert = htIdx >= 0 ? (pd[String(htIdx)]?.ht_certificate_number || pd['Heat Treatment']?.ht_certificate_number) : null;
                  const customerPO = selectedOrder.customer_order_number || null;
                  return (castNumber || htCert || customerPO) ? (
                    <div className="flex gap-4 flex-wrap mb-3 text-xs bg-slate-900/40 border border-slate-800 rounded px-3 py-2" data-testid="packaging-traceability">
                      {castNumber && (
                        <div><span className="text-slate-500 uppercase tracking-wider">Cast Number:</span> <span className="text-white font-mono font-bold" data-testid="pkg-cast-number">{castNumber}</span></div>
                      )}
                      {htCert && (
                        <div><span className="text-slate-500 uppercase tracking-wider">HT Certificate:</span> <span className="text-white font-mono font-bold" data-testid="pkg-ht-cert">{htCert}</span></div>
                      )}
                      {customerPO && (
                        <div><span className="text-slate-500 uppercase tracking-wider">Customer PO No:</span> <span className="text-white font-mono font-bold" data-testid="pkg-customer-po">{customerPO}</span></div>
                      )}
                    </div>
                  ) : null;
                })()}

                {packages.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-slate-500 uppercase tracking-wider border-b border-slate-800">
                          <th className="text-left py-2 px-2">Package ID</th>
                          <th className="text-left py-2 px-2">Type</th>
                          <th className="text-right py-2 px-2">Qty</th>
                          <th className="text-left py-2 px-2">Created</th>
                          <th className="text-left py-2 px-2">Slip</th>
                          <th className="text-right py-2 px-2">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-800/50">
                        {packages.map(pkg => (
                          <tr key={pkg.package_id} className="hover:bg-purple-500/5" data-testid={`package-row-${pkg.package_id}`}>
                            <td className="py-2 px-2 font-mono text-purple-400 font-bold">{pkg.package_id}</td>
                            <td className="py-2 px-2 text-white">{pkg.package_type_display || pkg.package_type}</td>
                            <td className="py-2 px-2 text-right text-white font-bold font-mono">
                              {editingPackage?.package_id === pkg.package_id ? (
                                <Input type="number" value={editingPackage.quantity} onChange={(e) => setEditingPackage(prev => ({...prev, quantity: e.target.value}))} className="w-20 h-6 text-xs bg-slate-900 border-slate-700 text-right inline-block" data-testid="edit-pkg-qty" />
                              ) : pkg.quantity}
                            </td>
                            <td className="py-2 px-2 text-slate-400">{pkg.created_at ? new Date(pkg.created_at).toLocaleDateString() : '-'}</td>
                            <td className="py-2 px-2">
                              {pkg.packing_slip ? (
                                <span className="text-green-400 text-[10px] font-mono" title={`Printed ${pkg.packing_slip.print_count}x`}>{pkg.packing_slip.ps_number}</span>
                              ) : (
                                <span className="text-slate-600 text-[10px]">—</span>
                              )}
                            </td>
                            <td className="py-2 px-2 text-right">
                              {editingPackage?.package_id === pkg.package_id ? (
                                <div className="flex gap-1 justify-end">
                                  <Button size="sm" onClick={handleUpdatePackage} className="h-6 px-2 text-[10px] bg-green-600 hover:bg-green-700" data-testid="save-pkg-edit">Save</Button>
                                  <Button size="sm" variant="ghost" onClick={() => setEditingPackage(null)} className="h-6 px-2 text-[10px] text-slate-400">Cancel</Button>
                                </div>
                              ) : (
                                <div className="flex gap-1 justify-end">
                                  <Button size="sm" variant="ghost" onClick={() => handlePrintPackingSlip(pkg)} className="h-6 w-6 p-0 text-green-400 hover:bg-green-500/10" title="Print Packing Slip" data-testid={`print-slip-${pkg.package_id}`}><Printer className="w-3 h-3" /></Button>
                                  <Button size="sm" variant="ghost" onClick={() => setEditingPackage({...pkg})} className="h-6 w-6 p-0 text-cyan-400 hover:bg-cyan-500/10" data-testid={`edit-pkg-${pkg.package_id}`}><FileEdit className="w-3 h-3" /></Button>
                                  <Button size="sm" variant="ghost" onClick={() => handleDeletePackage(pkg.package_id)} className="h-6 w-6 p-0 text-red-400 hover:bg-red-500/10" data-testid={`delete-pkg-${pkg.package_id}`}><Trash2 className="w-3 h-3" /></Button>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-slate-600 text-xs text-center py-3">No package units created yet</p>
                )}
              </div>

              {/* Fixed Action Buttons */}
              <div className="flex gap-3 pt-4 border-t border-slate-800 flex-shrink-0 bg-slate-950 flex-wrap">
                <Button
                  onClick={handleSaveProcessData}
                  data-testid="save-process-data-btn"
                  className="bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-400 border border-cyan-500/50 flex-1"
                >
                  Save Process Data
                </Button>
                {(() => {
                  // Check if any step is "In Progress"
                  for (let i = 0; i < selectedOrder.process_sequence.length; i++) {
                    const status = getProcessStatus(selectedOrder, i, selectedOrder.process_sequence[i]);
                    if (status === 'In Progress') return true;
                  }
                  return false;
                })() && (
                  <Button
                    onClick={handleCompleteCurrentStep}
                    data-testid="complete-step-btn"
                    className="bg-green-500/20 hover:bg-green-500/30 text-green-400 border border-green-500/50 font-bold uppercase tracking-wide px-6"
                  >
                    Complete Step
                  </Button>
                )}
                <Button
                  onClick={openSplitDialog}
                  data-testid="split-order-btn"
                  className="bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 border border-purple-500/50"
                >
                  <Scissors className="w-4 h-4 mr-1" /> Split Order
                </Button>
                {(selectedOrder.split_children?.length > 0 || selectedOrder.parent_order_number) && (
                  <Button
                    onClick={() => fetchSplitTree(selectedOrder.order_number)}
                    data-testid="view-split-tree-btn"
                    variant="outline"
                    className="border-indigo-500/50 text-indigo-400 hover:bg-indigo-500/10"
                  >
                    <GitBranch className="w-4 h-4 mr-1" /> Split Tree
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  className="border-slate-700 hover:border-slate-600"
                >
                  Close
                </Button>
              </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Shortfall Order Dialog */}
        <Dialog open={shortfallDialogOpen} onOpenChange={setShortfallDialogOpen}>
          <DialogContent className="bg-slate-950 border-orange-500/30 max-w-md" data-testid="shortfall-dialog">
            <DialogHeader>
              <DialogTitle className="text-white uppercase tracking-tight text-xl flex items-center gap-2">
                <AlertCircle className="w-6 h-6 text-orange-400" />
                Shortfall Detected
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4">
                <p className="text-white mb-2">
                  The final inspection quantity is <span className="font-bold text-orange-400">{shortfallQuantity}</span> units less than the order quantity.
                </p>
                <div className="grid grid-cols-2 gap-4 mt-4 text-sm">
                  <div>
                    <span className="text-slate-500">Order Quantity:</span>
                    <p className="text-white font-bold">{pendingShortfallOrder?.quantity}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Inspected Quantity:</span>
                    <p className="text-white font-bold">{pendingShortfallOrder ? pendingShortfallOrder.quantity - shortfallQuantity : 0}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Shortfall:</span>
                    <p className="text-orange-400 font-bold">{shortfallQuantity}</p>
                  </div>
                </div>
              </div>
              
              <p className="text-slate-400 text-sm">
                Would you like to create a new works order for the shortfall quantity?
              </p>
            </div>
            
            <div className="flex justify-end gap-3 pt-4 border-t border-slate-800">
              <Button
                variant="outline"
                onClick={handleDeclineShortfallOrder}
                className="border-slate-700 hover:border-slate-600"
                data-testid="decline-shortfall-btn"
              >
                No, Skip
              </Button>
              <Button
                onClick={handleCreateShortfallOrder}
                className="bg-orange-500/20 hover:bg-orange-500/30 text-orange-400 border border-orange-500/50 font-bold uppercase tracking-wide"
                data-testid="create-shortfall-btn"
              >
                Yes, Create Shortfall Order
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Split Order Dialog */}
        <Dialog open={splitDialogOpen} onOpenChange={setSplitDialogOpen}>
          <DialogContent className="bg-slate-950 border-purple-500/30 max-w-lg" data-testid="split-order-dialog">
            <DialogHeader>
              <DialogTitle className="text-white uppercase tracking-tight text-xl flex items-center gap-2">
                <Scissors className="w-5 h-5 text-purple-400" />
                Split Works Order
              </DialogTitle>
            </DialogHeader>

            {selectedOrder && (
              <div className="space-y-4">
                <div className="bg-purple-950/30 border border-purple-500/20 rounded-lg p-4">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div><span className="text-slate-500">Order:</span> <span className="text-white font-mono font-bold">{selectedOrder.order_number}</span></div>
                    <div><span className="text-slate-500">Current Qty:</span> <span className="text-white font-bold">{selectedOrder.quantity}</span></div>
                    <div><span className="text-slate-500">Customer:</span> <span className="text-white">{selectedOrder.customer}</span></div>
                    <div><span className="text-slate-500">Part:</span> <span className="text-white font-mono">{selectedOrder.part_number}</span></div>
                  </div>
                  {selectedOrder.original_quantity && selectedOrder.original_quantity !== selectedOrder.quantity && (
                    <div className="mt-2 pt-2 border-t border-purple-500/20 text-xs text-slate-400">
                      Original qty: {selectedOrder.original_quantity} | Already split: {selectedOrder.original_quantity - selectedOrder.quantity}
                    </div>
                  )}
                </div>

                <div>
                  <Label className="text-xs text-slate-500 uppercase tracking-wider mb-2 block">Split Quantity *</Label>
                  <Input
                    type="number"
                    min="1"
                    max={selectedOrder.quantity - 1}
                    value={splitData.split_quantity}
                    onChange={(e) => setSplitData(prev => ({ ...prev, split_quantity: e.target.value }))}
                    className="bg-slate-900/60 border-slate-800 text-white"
                    placeholder={`1 to ${selectedOrder.quantity - 1}`}
                    data-testid="split-quantity-input"
                  />
                  {splitData.split_quantity && (
                    <p className="text-xs text-slate-500 mt-1">
                      Split: <span className="text-purple-400 font-bold">{splitData.split_quantity}</span> | Remaining: <span className="text-cyan-400 font-bold">{selectedOrder.quantity - (parseInt(splitData.split_quantity) || 0)}</span>
                    </p>
                  )}
                </div>

                <div>
                  <Label className="text-xs text-slate-500 uppercase tracking-wider mb-2 block">Continue From Step *</Label>
                  <Select
                    value={String(splitData.continue_from_step_index)}
                    onValueChange={(val) => setSplitData(prev => ({ ...prev, continue_from_step_index: parseInt(val) }))}
                  >
                    <SelectTrigger className="bg-slate-900/60 border-slate-800" data-testid="split-step-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {selectedOrder.process_sequence.map((step, idx) => {
                        const stepStatus = getProcessStatus(selectedOrder, idx, step);
                        return (
                          <SelectItem key={idx} value={String(idx)}>
                            {step} ({stepStatus})
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500 mt-1">
                    Steps before this will be marked Complete on the new split order.
                  </p>
                </div>

                <div>
                  <Label className="text-xs text-slate-500 uppercase tracking-wider mb-2 block">Reason for Split</Label>
                  <Textarea
                    value={splitData.reason}
                    onChange={(e) => setSplitData(prev => ({ ...prev, reason: e.target.value }))}
                    className="bg-slate-900/60 border-slate-800 text-white min-h-[60px]"
                    placeholder="e.g. Urgent delivery, partial shipment, customer request..."
                    data-testid="split-reason-input"
                  />
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t border-slate-800">
                  <Button variant="outline" onClick={() => setSplitDialogOpen(false)} className="border-slate-700">Cancel</Button>
                  <Button
                    onClick={handleSplitOrder}
                    disabled={!splitData.split_quantity || parseInt(splitData.split_quantity) <= 0 || parseInt(splitData.split_quantity) >= selectedOrder.quantity}
                    className="bg-purple-600 hover:bg-purple-700 text-white font-bold uppercase tracking-wide disabled:opacity-50"
                    data-testid="confirm-split-btn"
                  >
                    <Scissors className="w-4 h-4 mr-2" /> Confirm Split
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Split Tree Dialog */}
        <Dialog open={splitTreeOpen} onOpenChange={setSplitTreeOpen}>
          <DialogContent className="bg-slate-950 border-indigo-500/30 max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="split-tree-dialog">
            <DialogHeader>
              <DialogTitle className="text-white uppercase tracking-tight text-xl flex items-center gap-2">
                <GitBranch className="w-5 h-5 text-indigo-400" />
                Split Order Tree
              </DialogTitle>
            </DialogHeader>

            {splitTreeData && (
              <div className="space-y-4">
                {/* Summary */}
                <div className="bg-indigo-950/30 border border-indigo-500/20 rounded-lg p-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <div className="text-center">
                      <p className="text-xs text-slate-500 uppercase">Original Qty</p>
                      <p className="text-xl font-bold font-mono text-white">{splitTreeData.summary.original_quantity}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-slate-500 uppercase">Parent Remaining</p>
                      <p className="text-xl font-bold font-mono text-cyan-400">{splitTreeData.summary.parent_remaining}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-slate-500 uppercase">Total Split</p>
                      <p className="text-xl font-bold font-mono text-purple-400">{splitTreeData.summary.total_split}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-slate-500 uppercase">Split Count</p>
                      <p className="text-xl font-bold font-mono text-indigo-400">{splitTreeData.summary.split_count}</p>
                    </div>
                  </div>
                </div>

                {/* Parent Order */}
                <div className="bg-slate-900/40 border border-cyan-500/20 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs px-2 py-0.5 rounded bg-cyan-500/20 text-cyan-400 font-bold uppercase">Parent</span>
                    <span className="font-mono text-white font-bold">{splitTreeData.root_order.order_number}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-xs text-slate-400">
                    <div>Qty: <span className="text-white font-bold">{splitTreeData.root_order.quantity}</span></div>
                    <div>Customer: <span className="text-white">{splitTreeData.root_order.customer}</span></div>
                    <div>Status: <span className="text-white">{splitTreeData.root_order.current_status}</span></div>
                  </div>
                </div>

                {/* Children */}
                {splitTreeData.children.map((child) => (
                  <div key={child.order_number} className="bg-slate-900/40 border border-purple-500/20 rounded-lg p-4 ml-6 relative">
                    <div className="absolute -left-4 top-1/2 -translate-y-1/2 w-4 h-0.5 bg-purple-500/30" />
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs px-2 py-0.5 rounded bg-purple-500/20 text-purple-400 font-bold uppercase">Split</span>
                      <span className="font-mono text-white font-bold">{child.order_number}</span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-xs text-slate-400">
                      <div>Qty: <span className="text-white font-bold">{child.quantity}</span></div>
                      <div>From: <span className="text-white">{child.split_from_step || '-'}</span></div>
                      <div>Status: <span className="text-white">{child.current_status}</span></div>
                    </div>
                    {child.split_reason && (
                      <p className="text-xs text-slate-500 mt-1">Reason: {child.split_reason}</p>
                    )}
                  </div>
                ))}

                {/* Audit Log */}
                {splitTreeData.audit?.length > 0 && (
                  <div className="border-t border-slate-800 pt-4">
                    <h4 className="text-xs text-slate-500 uppercase tracking-wider mb-2 font-bold">Split Audit Log</h4>
                    <div className="space-y-1">
                      {splitTreeData.audit.map((a, idx) => (
                        <div key={idx} className="text-xs text-slate-400 flex gap-2">
                          <span className="text-slate-600 font-mono">{a.timestamp?.slice(0, 19)}</span>
                          <span className="text-purple-400">{a.child_order_number}</span>
                          <span>qty={a.split_quantity} from {a.continue_from_step}</span>
                          <span className="text-slate-600">by {a.split_by}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Create Package Dialog */}
        <Dialog open={packageDialogOpen} onOpenChange={setPackageDialogOpen}>
          <DialogContent className="bg-slate-950 border-purple-500/30 max-w-md" data-testid="create-package-dialog">
            <DialogHeader>
              <DialogTitle className="text-white uppercase tracking-tight text-xl flex items-center gap-2">
                <Package className="w-5 h-5 text-purple-400" />
                Create Package Unit
              </DialogTitle>
            </DialogHeader>

            {selectedOrder && (
              <div className="space-y-4">
                <div className="bg-slate-900/60 border border-slate-800 rounded-lg p-3 grid grid-cols-2 gap-2 text-xs">
                  <div><span className="text-slate-500">WO:</span> <span className="text-white font-mono font-bold">{selectedOrder.order_number}</span></div>
                  <div><span className="text-slate-500">PO:</span> <span className="text-white font-mono">{selectedOrder.customer_order_number || '-'}</span></div>
                  {packageSummary && (
                    <>
                      <div><span className="text-slate-500">WO Qty:</span> <span className="text-white font-bold">{packageSummary.wo_quantity}</span></div>
                      <div><span className="text-slate-500">Packed:</span> <span className="text-purple-400 font-bold">{packageSummary.total_packed}</span> <span className="text-slate-600">Bal: {packageSummary.balance_remaining}</span></div>
                    </>
                  )}
                </div>

                <div>
                  <Label className="text-xs text-slate-500 uppercase tracking-wider mb-2 block">Package Type *</Label>
                  <Select value={newPackage.package_type} onValueChange={(val) => setNewPackage(prev => ({ ...prev, package_type: val }))}>
                    <SelectTrigger className="bg-slate-900/60 border-slate-800" data-testid="pkg-type-select">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Bag">Bag</SelectItem>
                      <SelectItem value="Drum">Drum</SelectItem>
                      <SelectItem value="Box">Box</SelectItem>
                      <SelectItem value="Pallet">Pallet</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {newPackage.package_type === 'Other' && (
                  <div>
                    <Label className="text-xs text-slate-500 uppercase tracking-wider mb-2 block">Custom Type</Label>
                    <Input value={newPackage.package_type_other} onChange={(e) => setNewPackage(prev => ({ ...prev, package_type_other: e.target.value }))} className="bg-slate-900/60 border-slate-800 text-white" placeholder="Specify type..." data-testid="pkg-type-other" />
                  </div>
                )}

                <div>
                  <Label className="text-xs text-slate-500 uppercase tracking-wider mb-2 block">Quantity *</Label>
                  <Input type="number" min="1" value={newPackage.quantity} onChange={(e) => setNewPackage(prev => ({ ...prev, quantity: e.target.value }))} className="bg-slate-900/60 border-slate-800 text-white" placeholder="Number of pieces in this package" data-testid="pkg-quantity-input" />
                </div>

                <div>
                  <Label className="text-xs text-slate-500 uppercase tracking-wider mb-2 block">Notes</Label>
                  <Textarea value={newPackage.notes} onChange={(e) => setNewPackage(prev => ({ ...prev, notes: e.target.value }))} className="bg-slate-900/60 border-slate-800 text-white min-h-[50px]" placeholder="Optional notes..." data-testid="pkg-notes-input" />
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t border-slate-800">
                  <Button variant="outline" onClick={() => setPackageDialogOpen(false)} className="border-slate-700">Cancel</Button>
                  <Button onClick={handleCreatePackage} disabled={!newPackage.package_type || !newPackage.quantity} className="bg-purple-600 hover:bg-purple-700 text-white font-bold uppercase tracking-wide disabled:opacity-50" data-testid="confirm-create-package-btn">
                    <Package className="w-4 h-4 mr-2" /> Create Package
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Pull from Stock Modal */}
        <PullFromStockModal
          open={pullFromStockOpen}
          onOpenChange={setPullFromStockOpen}
          order={selectedOrder}
          partDetails={partDetails}
          onSuccess={() => {
            // Refresh the WO data so cast number and process data update
            fetchData();
            if (selectedOrder) {
              axios.get(`${BACKEND_URL}/api/works-orders/${selectedOrder.order_number}`).then(res => {
                setSelectedOrder(res.data);
                setProcessData(res.data.process_data || {});
              });
            }
          }}
          user={user}
        />
        </div>
      </main>
    </div>
  );
};

export default KanbanBoard;